/**
 * How tightly a workspace screen is packed.
 *
 * Two settings, not a slider: "cozy" is the screen as designed, "compact" is the
 * same screen with the air taken out of it. An operator working a three hundred
 * message morning wants to see twenty conversations without scrolling; the same
 * operator writing one careful reply wants the roomy version back. Both are
 * right, which is why this is a preference and not a redesign.
 *
 * One setting for both workspace screens - mail and calls. They are the same
 * three-pane shape built from the same `crm-` vocabulary, and an operator who
 * works compact works compact; two switches would only mean two habits to keep
 * in step. The storage key still says `mail` because that is where the setting
 * started, and renaming it would quietly reset everybody who has already picked
 * compact.
 *
 * It is remembered, because it is a habit rather than a decision - somebody who
 * works compact works compact every day, and re-picking it every morning is a
 * small daily tax.
 *
 * Two mechanisms carry it, on purpose:
 *
 * - A class on the workspace root drives everything expressed in `crm-` CSS.
 *   That is most of it, and it costs no re-render. The rules are scoped
 *   `.crm-work.is-dense ...`, so a screen that does not put the class on its
 *   root is left alone.
 * - This hook carries the same fact into JavaScript, for the two kinds of value
 *   CSS cannot reach: a Tailwind utility already on an element (utilities win
 *   against our `@layer components` rules, so `p-4` cannot be overridden, only
 *   replaced) and `Avatar`'s size, which it writes as an inline style attribute.
 *
 * A module-level store rather than a context, because that second mechanism has
 * readers three and four levels down (`MessageBubble` inside `ThreadPane`) and a
 * provider would have to wrap - and re-indent - the whole workspace to reach
 * them. The preference is one per browser anyway, so a single store is not a
 * shortcut around scope, it is the actual shape of the thing.
 */
import { useCallback, useSyncExternalStore } from "react";

export type WorkDensity = "cozy" | "compact";

const STORAGE_KEY = "crm.mail.density";

function read(): WorkDensity {
  if (typeof window === "undefined") return "cozy";
  return window.localStorage.getItem(STORAGE_KEY) === "compact" ? "compact" : "cozy";
}

/** Read once at module load so the first paint is already the right density.
 *  Reading it in an effect instead paints the roomy list and snaps a frame
 *  later, which on this screen is the whole list jumping. */
let density: WorkDensity = read();
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function snapshot(): WorkDensity {
  return density;
}

function set(next: WorkDensity): void {
  if (next === density) return;
  density = next;
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
  for (const fn of listeners) fn();
}

/** The setting plus the switch, for the screen that owns the toggle. */
export function useWorkDensity(): [WorkDensity, () => void] {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  const toggle = useCallback(() => set(density === "compact" ? "cozy" : "compact"), []);
  return [value, toggle];
}

/** True when the workspace is packed tight. For the components that have to
 *  answer it in JavaScript rather than in CSS. */
export function useWorkCompact(): boolean {
  return useSyncExternalStore(subscribe, snapshot, snapshot) === "compact";
}
